import { ComponentProps } from "react"
import { Control, Controller, FieldPath, FieldValues } from "react-hook-form"
import { TextField } from "@mui/material"

type OptionalTextFieldProps<TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>> = Omit<ComponentProps<typeof TextField>, 'key'> & {
  control: Control<TFieldValues>;
  name: TName,
  label: ComponentProps<typeof TextField>['label'],
  onChangeValueTransformer?: (value: string) => string,
}

const OptionalTextField: React.FC<OptionalTextFieldProps> = (props) => {
  const { control, name, label, onChangeValueTransformer, ...textFieldProps } = props

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value, ref }, fieldState: { error } }) => {
        return <>
          <TextField
            {...textFieldProps}
            ref={ref}
            label={label}
            error={!!error}
            helperText={error && error.message}
            // If we have no value, we should represent that as an empty string in the form
            // This ensures this component always remains controlled
            value={value == null ? '' : value}
            // If we change the current value to an empty string, we should represent that as null in the form state
            onChange={event => {
              if (event.target.value === '') {
                onChange(null)
              } else if (event.target.value == null) {
                onChange(event.target.value)
              } else if (onChangeValueTransformer) {
                const transformedValue = onChangeValueTransformer(event.target.value)
                onChange(transformedValue)
              } else {
                onChange(event.target.value)
              }
            }}
          />
          {/* As we can't pass null through a form field, we pass an empty string. */}
          {/* If the form state is undefined, this will mean this field is ommitted. */}
          {/* This should mean that the form state is never an empty string - it will be `undefined | null | string{1+}`. */}
          {value !== undefined && <input type="hidden" name={name} value={value === null ? '' : value} />}
        </>
      }}
    />
  )
}

export default OptionalTextField
